import { useState, useRef, forwardRef } from 'react';
import { cn } from '../../lib/utils';

/**
 * Parse flexible date formats: DD-MM-YY, DD-MM-YYYY, DD/MM/YY, DD.MM.YY, DD-MM (auto FY year)
 * Returns YYYY-MM-DD string or null if invalid.
 * fyStartYear: if provided, used to auto-fill year for 2-part dates (DD-MM).
 */
function parseFlexDate(text, fyStartYear) {
  if (!text || !text.trim()) return null;
  const parts = text.trim().split(/[-/.]/);

  let day, month, year;

  if (parts.length === 2 && fyStartYear) {
    // DD-MM only — infer year from FY
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    if (isNaN(day) || isNaN(month)) return null;
    // Apr–Dec → start year, Jan–Mar → end year
    year = month >= 4 ? fyStartYear : fyStartYear + 1;
  } else if (parts.length === 3) {
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    if (year < 100) year += 2000;
  } else {
    return null;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return null;

  // Validate actual calendar date
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Convert YYYY-MM-DD → DD/MM/YYYY for display */
function toDisplay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return (y && m && d) ? `${d}/${m}/${y}` : iso;
}

export const DateInput = forwardRef(({ value, onChange, onKeyDown: parentKeyDown, className, fyStartYear, ...props }, ref) => {
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);

  const setRefs = (el) => {
    inputRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  const displayValue = editing ? text : toDisplay(value);

  const commitDate = (raw) => {
    const parsed = parseFlexDate(raw, fyStartYear);
    if (parsed && parsed !== value) {
      onChange({ target: { value: parsed } });
    }
  };

  const handleFocus = () => {
    setEditing(true);
    setText(toDisplay(value));
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleBlur = () => {
    if (!text.trim()) {
      if (value) onChange({ target: { value: '' } });
    } else {
      commitDate(text);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      commitDate(text);
    }
    parentKeyDown?.(e);
  };

  return (
    <input
      ref={setRefs}
      type="text"
      inputMode="text"
      value={displayValue}
      onChange={(e) => setText(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder="DD/MM/YYYY"
      autoComplete="off"
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      {...props}
    />
  );
});

DateInput.displayName = 'DateInput';
